import React, { createContext, useState, useEffect, useCallback } from "react";

export const SurveyContext = createContext();

export const SurveyProvider = ({ children }) => {
  // Core identifiers
  const [instanceId, setInstanceId] = useState(null);
  const [conditionId, setConditionId] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [caseNumber, setCaseNumber] = useState(null);

  // Experimental factors from URL (e.g., involvement, style, group)
  const [experimentalFactors, setExperimentalFactors] = useState({});

  // Instance state
  const [instanceStatus, setInstanceStatus] = useState(null); // initializing, populating, ready, active, completed, error
  const [instanceProgress, setInstanceProgress] = useState(null);
  const [isInstanceReady, setIsInstanceReady] = useState(false);

  // Initialization state
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(null);

  // Onboarding state
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [conditionConfig, setConditionConfig] = useState(null); // Full condition config including topic

  // Study mode state
  const [studyId, setStudyId] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [studyPhase, setStudyPhase] = useState(null); // "pre_survey" | "platform" | "post_survey" | "debriefing" | "complete"

  // Load from sessionStorage on mount (per-tab isolation — multiple participants
  // can be tested in separate tabs without cross-contamination)
  useEffect(() => {
    const stored = sessionStorage.getItem("spiders_session");
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.instanceId) setInstanceId(data.instanceId);
        if (data.conditionId) setConditionId(data.conditionId);
        if (data.participantId) setParticipantId(data.participantId);
        if (data.caseNumber) setCaseNumber(data.caseNumber);
        if (data.experimentalFactors) setExperimentalFactors(data.experimentalFactors);
        if (data.instanceStatus) setInstanceStatus(data.instanceStatus);
        if (["ready", "active"].includes(data.instanceStatus)) {
          setIsInstanceReady(true);
        }
        if (data.onboardingCompleted) setOnboardingCompleted(true);
        if (data.conditionConfig) setConditionConfig(data.conditionConfig);
        if (data.studyId) setStudyId(data.studyId);
        if (data.assignmentId) setAssignmentId(data.assignmentId);
        if (data.studyPhase) setStudyPhase(data.studyPhase);
      } catch (e) {
        console.error("Error loading session from sessionStorage:", e);
      }
    }
  }, []);

  // Save to sessionStorage when state changes
  useEffect(() => {
    if (instanceId || conditionId) {
      const data = {
        instanceId,
        conditionId,
        participantId,
        caseNumber,
        experimentalFactors,
        instanceStatus,
        onboardingCompleted,
        conditionConfig,
        studyId,
        assignmentId,
        studyPhase,
      };
      sessionStorage.setItem("spiders_session", JSON.stringify(data));
    }
  }, [instanceId, conditionId, participantId, caseNumber, experimentalFactors, instanceStatus, onboardingCompleted, conditionConfig, studyId, assignmentId, studyPhase]);

  // Update instance info
  const updateInstance = useCallback((instance) => {
    setInstanceId(instance._id || instance.instanceId);
    // Only update conditionId if the instance provides one (avoid overwriting with undefined)
    const newConditionId = instance.conditionId || instance.condition;
    if (newConditionId) {
      setConditionId(newConditionId);
    }
    setInstanceStatus(instance.status);
    setInstanceProgress(instance.populationProgress);
    setIsInstanceReady(["ready", "active"].includes(instance.status));
  }, []);

  // Update condition (for standalone mode selection)
  const updateCondition = useCallback((newConditionId) => {
    setConditionId(newConditionId);
  }, []);

  // Update participant info
  const updateParticipantInfo = useCallback((pId, cn) => {
    setParticipantId(pId);
    setCaseNumber(cn);
  }, []);

  // Update experimental factors
  const updateExperimentalFactors = useCallback((factors) => {
    setExperimentalFactors((prev) => ({ ...prev, ...factors }));
  }, []);

  // Set initialization complete
  const setInitialized = useCallback((success, error = null) => {
    setIsInitialized(true);
    if (error) {
      setInitializationError(error);
    }
  }, []);

  // Update instance status (for polling)
  const updateInstanceStatus = useCallback((status, progress = null) => {
    setInstanceStatus(status);
    if (progress) {
      setInstanceProgress(progress);
    }
    setIsInstanceReady(["ready", "active"].includes(status));
  }, []);

  // Mark onboarding as complete
  const completeOnboarding = useCallback(() => {
    setOnboardingCompleted(true);
  }, []);

  // Reset onboarding state (for new instances)
  const resetOnboarding = useCallback(() => {
    setOnboardingCompleted(false);
    setConditionConfig(null);
  }, []);

  // Update condition config (for onboarding flow)
  const updateConditionConfig = useCallback((config) => {
    setConditionConfig(config);
  }, []);

  // Update study mode state
  const updateStudyInfo = useCallback((sId, aId, phase) => {
    if (sId !== undefined) setStudyId(sId);
    if (aId !== undefined) setAssignmentId(aId);
    if (phase !== undefined) setStudyPhase(phase);
  }, []);

  // Clear all session data
  const clearSession = useCallback(() => {
    sessionStorage.removeItem("spiders_session");
    setInstanceId(null);
    setConditionId(null);
    setParticipantId(null);
    setCaseNumber(null);
    setExperimentalFactors({});
    setInstanceStatus(null);
    setInstanceProgress(null);
    setIsInstanceReady(false);
    setIsInitialized(false);
    setInitializationError(null);
    setOnboardingCompleted(false);
    setConditionConfig(null);
    setStudyId(null);
    setAssignmentId(null);
    setStudyPhase(null);
  }, []);

  // Check if we have minimum required data
  const hasRequiredData = instanceId && conditionId;

  // Legacy compatibility - expose condition
  const condition = conditionId;

  return (
    <SurveyContext.Provider
      value={{
        // Core identifiers
        instanceId,
        conditionId,
        condition, // Legacy alias
        participantId,
        caseNumber,

        // Experimental factors
        experimentalFactors,

        // Instance state
        instanceStatus,
        instanceProgress,
        isInstanceReady,

        // Initialization
        isInitialized,
        initializationError,
        hasRequiredData,

        // Onboarding
        onboardingCompleted,
        conditionConfig,
        completeOnboarding,
        resetOnboarding,
        updateConditionConfig,

        // Study mode
        studyId,
        assignmentId,
        studyPhase,
        updateStudyInfo,

        // Update functions
        updateInstance,
        updateCondition,
        updateParticipantInfo,
        updateExperimentalFactors,
        updateInstanceStatus,
        setInitialized,
        clearSession,

        // Legacy compatibility
        surveyToken: participantId,
        isConditionSet: !!conditionId,
        updateSurveyToken: (token) => setParticipantId(token),
        updateCaseNumber: (cn) => setCaseNumber(cn),
        clearSurveyData: clearSession,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};
